/**
 * RichEditor/RichEditor.jsx
 * ════════════════════════════════════════════════════════════════
 * Універсальний rich text редактор на contentEditable.
 * Без зовнішніх залежностей. Webpack 4 + Babel сумісний.
 *
 * ВИКОРИСТАННЯ:
 *   import RichEditor from '../RichEditor/RichEditor';
 *   import { RichPreview } from '../RichEditor/RichEditor';
 *
 *   // Мінімально:
 *   <RichEditor value={html} onChange={(html) => setHtml(html)} />
 *
 *   // З опціями:
 *   <RichEditor
 *     value={html}
 *     onChange={(html) => setHtml(html)}
 *     placeholder="Введіть текст..."
 *     maxLength={3000}
 *     minHeight={180}
 *     maxHeight={420}
 *     showCounter={true}
 *     hasError={false}
 *     disabled={false}
 *   />
 *
 *   // Тільки перегляд:
 *   <RichPreview html={html} />
 *
 * Props:
 *   value        {string}   — HTML вміст
 *   onChange     {function} — (html) => void
 *   placeholder  {string}   — текст заглушки
 *   maxLength    {number}   — ліміт символів (без HTML), 0 = без ліміту
 *   minHeight    {number}   — мін. висота px, default 160
 *   maxHeight    {number}   — макс. висота px, default 400
 *   showCounter  {boolean}  — показувати лічильник
 *   hasError     {boolean}  — зовнішня помилка (червона рамка)
 *   disabled     {boolean}  — тільки читання (без тулбару)
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';

// ════════════════════════════════════════════════════════════════
// СТИЛІ
// ════════════════════════════════════════════════════════════════

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        '.re-content{outline:none;word-break:break-word;}',
        '.re-content p{margin:0 0 10px;}',
        '.re-content p:last-child{margin-bottom:0;}',
        '.re-content h3{font-size:15px;font-weight:700;margin:14px 0 6px;color:#111827;}',
        '.re-content ul,.re-content ol{padding-left:22px;margin:8px 0;}',
        '.re-content li{margin-bottom:4px;}',
        '.re-content strong{font-weight:700;}',
        '.re-content em{font-style:italic;}',
        '.re-content u{text-decoration:underline;}',
        '.re-content[data-empty="true"]:before{content:attr(data-placeholder);color:#adb5bd;pointer-events:none;position:absolute;}',
        '.re-preview p{margin:0 0 10px;}',
        '.re-preview p:last-child{margin-bottom:0;}',
        '.re-preview h3{font-size:15px;font-weight:700;margin:14px 0 6px;color:#111827;}',
        '.re-preview ul,.re-preview ol{padding-left:22px;margin:8px 0;}',
        '.re-preview li{margin-bottom:4px;}',
    ].join('');
    document.head.appendChild(s);
}

// ════════════════════════════════════════════════════════════════
// УТИЛІТИ (експортовані)
// ════════════════════════════════════════════════════════════════

export function countChars(html) {
    if (!html) return 0;
    return html
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim().length;
}

// Конвертує plain text у HTML:
// - Подвійний перенос → новий параграф
// - Рядки що починаються з * або - → маркований список
// - Рядки що починаються з 1. 2. → нумерований список
export function convertPlainTextToHtml(text) {
    if (!text) return '';

    var esc = function(s) {
        return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    };

    // Розбиваємо на блоки по подвійному переносу
    var blocks = text.split(/\n{2,}/);
    var result = [];

    blocks.forEach(function(block) {
        var lines = block.split('\n').map(function(l) { return l.trim(); }).filter(Boolean);
        if (!lines.length) return;

        // Перевіряємо чи всі рядки є елементами маркованого списку
        var isBullet = lines.every(function(l) { return /^[\*\-•]\s+/.test(l); });
        var isOrdered = lines.every(function(l) { return /^\d+[\.\)]\s+/.test(l); });

        if (isBullet) {
            var items = lines.map(function(l) {
                return '<li>' + esc(l.replace(/^[\*\-•]\s+/, '')) + '</li>';
            }).join('');
            result.push('<ul>' + items + '</ul>');
        } else if (isOrdered) {
            var items2 = lines.map(function(l) {
                return '<li>' + esc(l.replace(/^\d+[\.\)]\s+/, '')) + '</li>';
            }).join('');
            result.push('<ol>' + items2 + '</ol>');
        } else {
            // Звичайний параграф — рядки з'єднуємо через <br>
            var content = lines.map(esc).join('<br>');
            result.push('<p>' + content + '</p>');
        }
    });

    return result.join('');
}

export function cleanPastedHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;

    var ALLOWED = {
        b:'b', strong:'strong', i:'i', em:'em', u:'u',
        h1:'h3', h2:'h3', h3:'h3',
        p:'p', br:'br', ul:'ul', ol:'ol', li:'li',
    };
    var UNWRAP = ['span','div','font','a','section','article','header',
                  'footer','table','tr','td','th','tbody','thead','tfoot'];

    function clean(node) {
        if (node.nodeType === 3) return node.cloneNode(false);
        if (node.nodeType !== 1) return null;
        var tag = node.tagName.toLowerCase();
        var kids = [];
        node.childNodes.forEach(function(c) {
            var r = clean(c);
            if (!r) return;
            if (r.nodeType === 11) { r.childNodes.forEach(function(x){ kids.push(x); }); }
            else kids.push(r);
        });
        if (tag === 'br') return document.createElement('br');
        if (UNWRAP.indexOf(tag) !== -1) {
            if (tag === 'div') {
                var hasBlock = kids.some(function(k){ return k.nodeType===1 && ['p','h3','ul','ol'].indexOf(k.tagName.toLowerCase())!==-1; });
                if (!hasBlock && kids.length) {
                    var p = document.createElement('p');
                    kids.forEach(function(k){ p.appendChild(k); });
                    return p;
                }
            }
            var f = document.createDocumentFragment();
            kids.forEach(function(k){ f.appendChild(k); });
            return f;
        }
        var newTag = ALLOWED[tag];
        if (!newTag) { var f2=document.createDocumentFragment(); kids.forEach(function(k){f2.appendChild(k);}); return f2; }
        var el = document.createElement(newTag);
        kids.forEach(function(k){ el.appendChild(k); });
        return el;
    }

    var out = document.createElement('div');
    tmp.childNodes.forEach(function(c){ var r=clean(c); if(r) out.appendChild(r); });
    out.querySelectorAll('p,h3').forEach(function(el){
        if(!el.textContent.trim()&&!el.querySelector('br')) el.remove();
    });
    return out.innerHTML || '';
}

// ════════════════════════════════════════════════════════════════
// ТУЛБАР
// ════════════════════════════════════════════════════════════════

function Btn(props) {
    return (
        <button
            type="button"
            onMouseDown={function(e){ e.preventDefault(); props.onClick(); }}
            title={props.title}
            style={{
                width:30, height:28,
                border:'1px solid '+(props.active?'#0069ff':'#ced4da'),
                borderRadius:5,
                background:props.active?'#e6f0ff':'#fff',
                color:props.active?'#0069ff':'#495057',
                cursor:'pointer',
                display:'inline-flex', alignItems:'center', justifyContent:'center',
                fontSize:props.fs||12,
                fontWeight:props.fw||'normal',
                fontStyle:props.fi||'normal',
                textDecoration:props.td||'none',
                flexShrink:0,
            }}
        >{props.children}</button>
    );
}
function Sep(){ return <div style={{width:1,height:20,background:'#dee2e6',margin:'0 2px'}}/>; }

// ════════════════════════════════════════════════════════════════
// ОСНОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var RichEditor = function(props) {
    var value       = props.value       || '';
    var onChange    = props.onChange    || function(){};
    var placeholder = props.placeholder || 'Введіть текст...';
    var maxLength   = props.maxLength   || 0;
    var minHeight   = props.minHeight   || 160;
    var maxHeight   = props.maxHeight   || 400;
    var hasError    = props.hasError    || false;
    var disabled    = props.disabled    || false;
    var showCounter = props.showCounter !== undefined ? props.showCounter : maxLength > 0;

    var ref         = useRef(null);
    var initialized = useRef(false);
    var [isEmpty, setIsEmpty] = useState(!value);
    var [tick, setTick]       = useState(0); // для active стану кнопок

    useEffect(function(){ injectStyles(); }, []);

    useEffect(function(){
        if (ref.current && !initialized.current) {
            ref.current.innerHTML = value || '';
            initialized.current = true;
            setIsEmpty(!value);
        }
    }, []);

    useEffect(function(){
        if (!initialized.current) return;
        if (!value && ref.current && ref.current.innerHTML) {
            ref.current.innerHTML = '';
            setIsEmpty(true);
        }
    }, [value]);

    var exec = function(cmd, val) {
        document.execCommand(cmd, false, val||null);
        ref.current && ref.current.focus();
        setTick(function(t){ return t+1; });
    };

    var isActive = function(cmd, val) {
        try { return val ? document.queryCommandValue(cmd)===val : document.queryCommandState(cmd); }
        catch(e){ return false; }
    };

    var handleInput = function() {
        if (!ref.current) return;
        var html  = ref.current.innerHTML;
        var empty = !html || html==='<br>' || html==='<p><br></p>';
        setIsEmpty(empty);
        onChange(empty ? '' : html);
    };

    var handlePaste = function(e) {
        e.preventDefault();
        var html = e.clipboardData ? e.clipboardData.getData('text/html') : '';

        var cleaned = '';
        if (!html) {
            // Plain text — конвертуємо в HTML зі списками
            var text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
            cleaned = convertPlainTextToHtml(text);
        } else {
            cleaned = cleanPastedHtml(html);
        }

        if (!cleaned) return;

        // Замість execCommand('insertHTML') який браузер може обрізати —
        // вставляємо через Selection API для точного розміщення
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
            // Якщо нема виділення — просто додаємо в кінець
            if (ref.current) {
                var tmp = document.createElement('div');
                tmp.innerHTML = cleaned;
                while (tmp.firstChild) {
                    ref.current.appendChild(tmp.firstChild);
                }
            }
        } else {
            var range = sel.getRangeAt(0);
            range.deleteContents();
            var frag = document.createRange().createContextualFragment(cleaned);
            range.insertNode(frag);
            // Переміщуємо курсор в кінець вставленого
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }

        handleInput();
    };

    var handleKeyDown = function(e) {
        if (!maxLength) return;
        var chars = countChars(ref.current ? ref.current.innerHTML : '');
        var nav   = ['Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown'];
        if (chars >= maxLength && e.key.length===1 && nav.indexOf(e.key)===-1 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    var charCount = countChars(value);
    var isOver    = maxLength > 0 && charCount > maxLength;
    var near      = maxLength > 0 && charCount > maxLength * 0.85;
    var border    = isOver || hasError ? '#dc3545' : '#ced4da';

    return (
        <div>
            {!disabled && (
                <div style={{
                    display:'flex', gap:3, alignItems:'center',
                    padding:'5px 8px',
                    border:'1px solid '+border, borderBottom:'none',
                    borderRadius:'6px 6px 0 0',
                    background:'#f8f9fa', flexWrap:'wrap',
                }}>
                    <Btn onClick={function(){ exec('bold'); }}      active={isActive('bold')}      title="Жирний (Ctrl+B)"       fw="700">Ж</Btn>
                    <Btn onClick={function(){ exec('italic'); }}    active={isActive('italic')}    title="Курсив (Ctrl+I)"       fi="italic">К</Btn>
                    <Btn onClick={function(){ exec('underline'); }} active={isActive('underline')} title="Підкреслений (Ctrl+U)" td="underline">П</Btn>
                    <Sep/>
                    <Btn onClick={function(){ exec('formatBlock','h3'); }} active={isActive('formatBlock','h3')} title="Підзаголовок" fw="700" fs={11}>H3</Btn>
                    <Btn onClick={function(){ exec('formatBlock','p'); }}  active={false}                        title="Звичайний текст">¶</Btn>
                    <Sep/>
                    <Btn onClick={function(){ exec('insertUnorderedList'); }} active={isActive('insertUnorderedList')} title="Маркований список">•—</Btn>
                    <Btn onClick={function(){ exec('insertOrderedList'); }}   active={isActive('insertOrderedList')}   title="Нумерований список">1.</Btn>
                    <Sep/>
                    <Btn onClick={function(){ exec('removeFormat'); exec('formatBlock','p'); }} active={false} title="Очистити форматування" fs={10}>A↺</Btn>
                </div>
            )}

            <div
                ref={ref}
                contentEditable={!disabled}
                suppressContentEditableWarning
                className="re-content"
                data-placeholder={placeholder}
                data-empty={isEmpty?'true':'false'}
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onKeyUp={function(){ setTick(function(t){ return t+1; }); }}
                onMouseUp={function(){ setTick(function(t){ return t+1; }); }}
                style={{
                    border:'1px solid '+border,
                    borderRadius: disabled ? 6 : '0 0 6px 6px',
                    background: disabled ? '#f9fafb' : '#fff',
                    padding:'12px 14px',
                    fontSize:14, lineHeight:1.75, color:'#212529',
                    minHeight:minHeight, maxHeight:maxHeight,
                    overflowY:'auto', position:'relative',
                    transition:'border-color 0.15s',
                    cursor: disabled ? 'default' : 'text',
                }}
            />

            {showCounter && (
                <div style={{display:'flex',justifyContent:'space-between',marginTop:4,fontSize:11}}>
                    <span style={{color:'#9ca3af'}}>
                        Ctrl+B — жирний · Ctrl+I — курсив · Вставка зберігає форматування
                    </span>
                    <span style={{color:isOver?'#dc3545':near?'#e67e22':'#9ca3af',fontWeight:isOver||near?600:400}}>
                        {charCount}{maxLength>0?' / '+maxLength:''}
                    </span>
                </div>
            )}
            {isOver && (
                <div style={{fontSize:12,color:'#dc3545',marginTop:2}}>
                    Перевищено ліміт на {charCount-maxLength} символів
                </div>
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
// КОМПОНЕНТ ПЕРЕГЛЯДУ
// ════════════════════════════════════════════════════════════════

export var RichPreview = function(props) {
    var html  = props.html  || '';
    var style = props.style || {};
    if (!html) return null;
    return (
        <div
            className="re-preview"
            style={Object.assign({fontSize:14,lineHeight:1.75,color:'#374151'},style)}
            dangerouslySetInnerHTML={{__html:html}}
        />
    );
};

export default RichEditor;